import React, { useCallback, useEffect, useState } from "react";
import { Mail, Copy, Trash2, Check, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const InviteFriends = () => {
  const { createInvite, listMyInvites, revokeInvite } = useAuth();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [inviteeEmail, setInviteeEmail] = useState("");
  const [copiedCode, setCopiedCode] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    const r = await listMyInvites();
    setBusy(false);
    if (r.ok) setInvites(r.data.invites || []);
  }, [listMyInvites]);

  useEffect(() => {
    load();
  }, [load]);

  const onGenerate = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await createInvite(inviteeEmail.trim());
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setInviteeEmail("");
    await load();
  };

  const onCopy = async (url, code) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(""), 1500);
    } catch {
      /* ignore */
    }
  };

  const onRevoke = async (code) => {
    if (!window.confirm("Revoke this invite link? Anyone with the link won't be able to use it.")) return;
    setBusy(true);
    await revokeInvite(code);
    await load();
    setBusy(false);
  };

  return (
    <section className="invite-panel" data-testid="invite-panel">
      <header className="invite-panel-header">
        <div>
          <h3 className="invite-panel-title">
            <Mail size={16} /> Invite a friend
          </h3>
          <p className="invite-panel-sub">
            Generate a personal invite link. Anyone you share it with can sign up faster — but an admin still needs to approve them.
          </p>
        </div>
        <button
          type="button"
          className="btn-ghost invite-refresh"
          onClick={load}
          disabled={busy}
          data-testid="invite-refresh"
          aria-label="Refresh invites"
        >
          <RefreshCw size={14} />
        </button>
      </header>

      <form className="invite-form" onSubmit={onGenerate}>
        <input
          type="email"
          value={inviteeEmail}
          onChange={(e) => setInviteeEmail(e.target.value)}
          placeholder="friend@example.com (optional label)"
          data-testid="invite-email-input"
        />
        <button
          type="submit"
          className="btn-solid"
          disabled={busy}
          data-testid="invite-generate-btn"
        >
          {busy ? "…" : "Generate link"}
        </button>
      </form>
      {err && <div className="auth-error" data-testid="invite-error">{err}</div>}

      {invites.length === 0 ? (
        <div className="invite-empty">No invites yet — send your first one above.</div>
      ) : (
        <ul className="invite-list">
          {invites.map((i) => {
            const isUsed = !!i.used_at;
            const isExpired = i.expired;
            return (
              <li
                key={i.code}
                className={`invite-row${isUsed ? " used" : ""}${isExpired ? " expired" : ""}`}
                data-testid={`invite-row-${i.code}`}
              >
                <div className="invite-row-main">
                  <code className="invite-code">{i.code}</code>
                  {i.invitee_email && (
                    <div className="invite-label">for {i.invitee_email}</div>
                  )}
                  {isUsed ? (
                    <div className="invite-status invite-status-used">
                      Used by {i.used_by_email} — awaiting admin approval
                    </div>
                  ) : isExpired ? (
                    <div className="invite-status invite-status-expired">Expired</div>
                  ) : (
                    <div className="invite-status invite-status-open">Open</div>
                  )}
                </div>
                <div className="invite-row-actions">
                  {!isUsed && !isExpired && (
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => onCopy(i.url, i.code)}
                      data-testid={`invite-copy-${i.code}`}
                    >
                      {copiedCode === i.code ? (
                        <>
                          <Check size={14} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Copy link
                        </>
                      )}
                    </button>
                  )}
                  {!isUsed && (
                    <button
                      type="button"
                      className="btn-ghost btn-danger"
                      onClick={() => onRevoke(i.code)}
                      data-testid={`invite-revoke-${i.code}`}
                    >
                      <Trash2 size={14} /> Revoke
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default InviteFriends;
